import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { motion } from 'framer-motion';
import { HelpCircle } from 'lucide-react';

export function PlansFAQ() {
  return (
    <div id="plans-faq" className="bg-muted/50 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-1/4 top-0 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-1/4 bottom-0 h-32 w-32 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="relative pt-20">
        <div className="mx-auto max-w-7xl">
          <div className="p-8 sm:p-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h2 className="flex items-center justify-center gap-3">
                <HelpCircle className="h-8 w-8 text-primary" />
                <span className="text-muted-foreground text-3xl font-light">
                  Frequently Asked Questions
                </span>
              </h2>
            </motion.div>

            <div className="mt-12"></div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-1 gap-8 md:grid-cols-2"
            >
              <div className="space-y-4">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1" className="border-border/50">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="text-lg font-medium">
                        How do subscriptions work?
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        Subscriptions give you the best deal on AI generation
                        features, including image generation, custom model
                        training, and premium styles.
                        <br />
                        <br />
                        Subscriptions are monthly or annual, with annual
                        commitment plans providing higher discounts.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  {/* <AccordionItem value="item-2" className="border-border/50">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="text-lg font-medium">
                        How does the free trial work?
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        The free trial allows you to try out our AI generation
                        features at no cost.
                        <br />
                        <br />
                        You can cancel risk-free during your trial without being
                        charged. Any generations created during the free trial
                        period are yours to keep.
                        <br />
                        <br />
                        After your trial ends, your subscription begins and you
                        will automatically be charged the rate listed in your
                        plan.
                      </p>
                    </AccordionContent>
                  </AccordionItem> */}

                  <AccordionItem value="item-3" className="border-border/50">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="text-lg font-medium">
                        What features are included in a subscription?
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        Subscription plans provide access to AI image
                        generation, custom model training, and premium styles.
                        <br />
                        <br />
                        Most plans also provide access to advanced features and
                        priority support.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-4" className="border-border/50">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="text-lg font-medium">
                        What happens to my unused credits?
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        Unused credits, up to the defined limit of your plan,
                        will roll over to the next month as long as your
                        subscription is active.
                        <br />
                        <br />
                        If you cancel your subscription, you&apos;ll lose any
                        unused credits at the end of the billing cycle.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <div className="space-y-4">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-5" className="border-border/50">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="text-lg font-medium">
                        What can I use my credits for?
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        Credits can be used for any AI generation feature on the
                        platform.
                        <br />
                        <br />
                        Credits remain valid for one year from the date you
                        purchase them. You can purchase as many as you like,
                        including discounted credits that come with a
                        subscription.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-6" className="border-border/50">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="text-lg font-medium">
                        Do credits expire?
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        Yes, unused credits automatically expire 12 months from
                        the date of purchase.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-7" className="border-border/50">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="text-lg font-medium">
                        Should I purchase a subscription plan or a credit pack?
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        Subscriptions are best if you have ongoing AI generation
                        needs. Most of our plans provide access to our entire
                        collection at a discount.
                        <br />
                        <br />
                        Credit packs are designed for those who have fluctuating
                        AI generation needs.
                        <br />
                        <br />
                        You may purchase both credit packs and subscription
                        plans individually or buy both.
                      </p>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="item-8" className="border-border/50">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="text-lg font-medium">
                        What happens if I cancel my subscription?
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        If you cancel your subscription, you&apos;ll lose
                        access to the subscription features and forfeit your
                        unused credits at the end of the billing cycle.
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <div className="p-1"></div>
    </div>
  );
}
